import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/input';
import { SelectItem } from '@shared/components/ui/select';
import { useCountryTranslation } from '@shared/hooks/useCountryTranslation';
import React from 'react';
import { LocalFilters } from '../../hooks/usePendingOrders';
import { SelectWithClear } from './SelectWithClear';

interface FiltersSectionProps {
  filters: LocalFilters;
  updateFilters: (filters: Partial<LocalFilters>) => void;
  uniqueValues: {
    couriers: string[];
    countries: string[];
    shops: string[];
    orderTypes: string[];
    teamNames: string[];
  };
  isInsideAccordion?: boolean;
}

export const FiltersSection: React.FC<FiltersSectionProps> = ({
  filters,
  updateFilters,
  uniqueValues,
  isInsideAccordion = false,
}) => {
  // 🌍 Hook para traducir países
  const { getCountryName } = useCountryTranslation();

  // Si está dentro del acordeón, solo mostrar el contenido sin el contenedor card
  if (isInsideAccordion) {
    return (
      <div className="mt-4 space-y-4">
        {/* Buscador */}
        <div>
          <label className="text-sm font-medium text-foreground mb-2 block">Buscar producto</label>
          <div className="relative">
            <Input
              type="text"
              placeholder="Buscar por nombre o código..."
              value={filters.searchTerm}
              onChange={(e) => updateFilters({ searchTerm: e.target.value })}
              className="pr-8"
            />

            {filters.searchTerm && (
              <Button
                variant="ghost"
                size="sm"
                className="absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6 p-0 hover:bg-transparent"
                onClick={() => updateFilters({ searchTerm: '' })}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="text-muted-foreground hover:text-foreground"
                >
                  <path d="m18 6-12 12" />
                  <path d="m6 6 12 12" />
                </svg>
              </Button>
            )}
          </div>
        </div>

        {/* Filtros en grid responsive */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Filtro por Mensajería */}
          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">Mensajería</label>
            <SelectWithClear
              value={filters.courier}
              onValueChange={(value) => updateFilters({ courier: value })}
              onClear={() => updateFilters({ courier: '' })}
              placeholder="Todas las mensajerías"
            >
              {uniqueValues.couriers.map((courier) => (
                <SelectItem key={courier} value={courier}>
                  {courier}
                </SelectItem>
              ))}
            </SelectWithClear>
          </div>

          {/* Filtro por País */}
          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">País</label>
            <SelectWithClear
              value={filters.country}
              onValueChange={(value) => updateFilters({ country: value })}
              onClear={() => updateFilters({ country: '' })}
              placeholder="Todos los países"
            >
              {uniqueValues.countries.map((country) => (
                <SelectItem key={country} value={country}>
                  {getCountryName(country)}
                </SelectItem>
              ))}
            </SelectWithClear>
          </div>

          {/* Filtro por Tienda */}
          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">Tienda</label>
            <SelectWithClear
              value={filters.shop}
              onValueChange={(value) => updateFilters({ shop: value })}
              onClear={() => updateFilters({ shop: '' })}
              placeholder="Todas las tiendas"
            >
              {uniqueValues.shops.map((shop) => (
                <SelectItem key={shop} value={shop}>
                  {shop}
                </SelectItem>
              ))}
            </SelectWithClear>
          </div>

          {/* Filtro por Tipo de Pedido */}
          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">Tipo de Pedido</label>
            <SelectWithClear
              value={filters.orderType}
              onValueChange={(value) => updateFilters({ orderType: value })}
              onClear={() => updateFilters({ orderType: '' })}
              placeholder="Todos los tipos"
            >
              {uniqueValues.orderTypes.map((orderType) => (
                <SelectItem key={orderType} value={orderType}>
                  {orderType === 'simple' ? 'Simple' : orderType === 'multiple' ? 'Múltiple' : orderType}
                </SelectItem>
              ))}
            </SelectWithClear>
          </div>

          {/* Filtro por Equipo */}
          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">Equipo</label>
            <SelectWithClear
              value={filters.teamName}
              onValueChange={(value) => updateFilters({ teamName: value })}
              onClear={() => updateFilters({ teamName: '' })}
              placeholder="Todos los clientes"
            >
              {uniqueValues.teamNames.map((teamName) => (
                <SelectItem key={teamName} value={teamName}>
                  {teamName}
                </SelectItem>
              ))}
            </SelectWithClear>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg p-4">
      <h3 className="text-lg font-semibold text-foreground mb-4">Filtros</h3>

      <div className="space-y-4 md:space-y-4">
        {/* Buscador */}
        <div>
          <label className="text-sm font-medium text-foreground mb-2 block">Buscar producto</label>
          <div className="relative">
            <Input
              type="text"
              placeholder="Buscar por nombre o código..."
              value={filters.searchTerm}
              onChange={(e) => updateFilters({ searchTerm: e.target.value })}
              className="pr-8"
            />

            {filters.searchTerm && (
              <Button
                variant="ghost"
                size="sm"
                className="absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6 p-0 hover:bg-transparent"
                onClick={() => updateFilters({ searchTerm: '' })}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="text-muted-foreground hover:text-foreground"
                >
                  <path d="m18 6-12 12" />
                  <path d="m6 6 12 12" />
                </svg>
              </Button>
            )}
          </div>
        </div>

        {/* Filtros responsive: stack vertical en desktop, grid en mobile dentro de acordeón */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-1 gap-4">
          {/* Filtro por Mensajería */}
          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">Mensajería</label>
            <SelectWithClear
              value={filters.courier}
              onValueChange={(value) => updateFilters({ courier: value })}
              onClear={() => updateFilters({ courier: '' })}
              placeholder="Todas las mensajerías"
            >
              {uniqueValues.couriers.map((courier) => (
                <SelectItem key={courier} value={courier}>
                  {courier}
                </SelectItem>
              ))}
            </SelectWithClear>
          </div>

          {/* Filtro por País */}
          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">País</label>
            <SelectWithClear
              value={filters.country}
              onValueChange={(value) => updateFilters({ country: value })}
              onClear={() => updateFilters({ country: '' })}
              placeholder="Todos los países"
            >
              {uniqueValues.countries.map((country) => (
                <SelectItem key={country} value={country}>
                  {getCountryName(country)}
                </SelectItem>
              ))}
            </SelectWithClear>
          </div>

          {/* Filtro por Tienda */}
          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">Tienda</label>
            <SelectWithClear
              value={filters.shop}
              onValueChange={(value) => updateFilters({ shop: value })}
              onClear={() => updateFilters({ shop: '' })}
              placeholder="Todas las tiendas"
            >
              {uniqueValues.shops.map((shop) => (
                <SelectItem key={shop} value={shop}>
                  {shop}
                </SelectItem>
              ))}
            </SelectWithClear>
          </div>

          {/* Filtro por Tipo de Pedido */}
          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">Tipo de Pedido</label>
            <SelectWithClear
              value={filters.orderType}
              onValueChange={(value) => updateFilters({ orderType: value })}
              onClear={() => updateFilters({ orderType: '' })}
              placeholder="Todos los tipos"
            >
              {uniqueValues.orderTypes.map((orderType) => (
                <SelectItem key={orderType} value={orderType}>
                  {orderType === 'simple' ? 'Simple' : orderType === 'multiple' ? 'Múltiple' : orderType}
                </SelectItem>
              ))}
            </SelectWithClear>
          </div>

          {/* Filtro por Equipo */}
          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">Equipo</label>
            <SelectWithClear
              value={filters.teamName}
              onValueChange={(value) => updateFilters({ teamName: value })}
              onClear={() => updateFilters({ teamName: '' })}
              placeholder="Todos los clientes"
            >
              {uniqueValues.teamNames.map((teamName) => (
                <SelectItem key={teamName} value={teamName}>
                  {teamName}
                </SelectItem>
              ))}
            </SelectWithClear>
          </div>
        </div>
      </div>
    </div>
  );
};
